import {
  Button,
  Column,
  Loader,
  Modal,
  Row,
  Text,
  useDesignTokens,
} from "@gradience/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import useSchool from "../../../lib/use-school";
import { hasPaymentMethod } from "../../../domain/is-school-info-complete";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { SetupIntent, StripeError } from "@stripe/stripe-js";
import {
  queryKeys,
  useApiPost,
  useApiPut,
} from "../../../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  displayPaymentMethod,
  displayPaymentMethodExpiry,
} from "../../../lib/display-payment-method";

const BillingMethodModal = () => {
  const search = useSearch({ from: "/$test-slug/home/" });
  const navigate = useNavigate({ from: "/" });
  const designTokens = useDesignTokens();
  const school = useSchool();

  const [stripeError, setStripeError] = useState<StripeError | undefined>();
  const stripe = useStripe();
  const elements = useElements();
  const postSetupIntent = useApiPost("/setup-intents");
  const putSchool = useApiPut("/school");

  const queryClient = useQueryClient();

  const onClose = () =>
    navigate({ search: (prev) => ({ ...prev, modal: undefined }) });
  const isOpen = search.modal === "billing-method";

  const submitting = postSetupIntent.isPending || putSchool.isPending;
  const currentPaymentMethod = school.data?.paymentMethod;

  const submitForm = async () => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      return;
    }

    const res = await postSetupIntent.mutateAsync({ body: {} });

    const { clientSecret } = res;

    let error: StripeError | undefined = undefined;
    let setupIntent: SetupIntent | undefined = undefined;

    // Confirm the SetupIntent using the details collected by the Payment Element
    const confirmSetupResult = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        return_url: "https://example.com/complete",
      },
      redirect: "if_required",
    });
    error = confirmSetupResult.error;
    setupIntent = confirmSetupResult.setupIntent;

    if (error) {
      setStripeError(error);
    } else {
      await putSchool.mutateAsync({
        body: {
          ...(setupIntent
            ? {
                paymentMethod:
                  typeof setupIntent.payment_method === "string"
                    ? { id: setupIntent.payment_method }
                    : undefined,
              }
            : {}),
        },
      });

      queryClient.invalidateQueries({ queryKey: queryKeys["/school"] });

      onClose();
    }
  };

  return (
    <Modal open={isOpen} close={onClose}>
      {school.isLoading ? (
        <Loader />
      ) : (
        <form
          key={isOpen ? "open" : "closed"}
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
          style={{ flex: 1, gap: 32, display: "flex", flexDirection: "column" }}
        >
          <Row gap={16}>
            <Text textStyle="24px - Bold" style={{ flex: 1 }}>
              {hasPaymentMethod(school.data)
                ? "Edit Billing Info"
                : "Add Billing Info"}
            </Text>
          </Row>
          {currentPaymentMethod ? (
            <Column gap={8}>
              <Text textStyle="16px - Semibold">Current Payment Method</Text>
              <Row
                gap={8}
                style={{
                  alignItems: "center",
                  padding: 12,
                  borderRadius: 8,
                  border: `1px solid ${designTokens.colors.border.default}`,
                }}
              >
                <Text textStyle="14px - Semibold" style={{ flex: 1 }}>
                  {displayPaymentMethod(currentPaymentMethod)}
                </Text>
                {displayPaymentMethodExpiry(currentPaymentMethod) && (
                  <Text textStyle="14px - Medium">
                    {displayPaymentMethodExpiry(currentPaymentMethod)}
                  </Text>
                )}
              </Row>
              <Text textStyle="14px - Medium">
                Registration charges are made to this payment method. To use a
                different one, enter it below.
              </Text>
            </Column>
          ) : (
            <Text>
              <Text>Add a payment method before registering for the ULE.</Text>
            </Text>
          )}
          <Column gap={8} style={{ flex: 1 }}>
            <Text textStyle="16px - Semibold">
              {currentPaymentMethod
                ? "New Payment Method"
                : "Billing Information*"}
            </Text>
            <Text textStyle="14px - Medium">
              Curious about alternative payment methods? Please email{" "}
              <a
                href="mailto:uleinfo@instituteforclassicallanguages.org"
                style={{
                  color: designTokens.colors.text.brand,
                  textDecoration: "none",
                  fontWeight: "bold",
                }}
              >
                uleinfo@instituteforclassicallanguages.org
              </a>
              .
            </Text>
            <PaymentElement />
            {stripeError && (
              <Text style={{ color: designTokens.colors.text.brand }}>
                {stripeError.message}
              </Text>
            )}
          </Column>
          <Row gap={16}>
            <Button
              type="submit"
              disabled={!stripe}
              loading={submitting}
              text={currentPaymentMethod ? "Replace" : "Add"}
              variant="primary"
              size="large"
            />
            <Button
              type="button"
              text="Cancel"
              disabled={submitting}
              linkProps={{
                search: (prev) => ({ ...prev, modal: undefined }),
              }}
              size="large"
            />
          </Row>
        </form>
      )}
    </Modal>
  );
};

export default BillingMethodModal;
